import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Box, Stack } from '@mui/material';

import { styles } from './index.styles';

export const PERCENTAGE_DOUGHNUT_LABEL_TEST_ID = 'percentage-doughnut-label';

interface PercentageDoughnutProps {
  denominator?: number;
  nominator: number;
  showNA?: boolean;
}

const PercentageDoughnut: FC<PercentageDoughnutProps> = ({
  nominator,
  denominator = 100,
  showNA,
}) => {
  const percentage = useMemo(
    () => Math.round((100 * nominator) / (denominator === 0 ? 1 : denominator)),
    [nominator, denominator],
  );

  return (
    <Box sx={styles.backdrop}>
      {/* The first doughnut is a grey backdrop without animation */}
      <Doughnut
        data={{
          datasets: [
            {
              data: [1],
              backgroundColor: ['#999'],
              borderWidth: 0,
            },
          ],
        }}
        options={{
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          maintainAspectRatio: false,
          responsive: true,
          cutout: 60,
          animation: { animateRotate: false },
        }}
      />
      <Box sx={styles.doughnut}>
        <Doughnut
          data={{
            datasets: [
              {
                data: [nominator, denominator - nominator],
                backgroundColor: ['#fc0', 'rgba(0, 0, 0, 0)'],
                borderColor: ['#fc0', 'rgba(0, 0, 0, 0)'],
                borderWidth: 1,
              },
            ],
          }}
          options={{
            plugins: {
              legend: { display: false },
              tooltip: { enabled: false },
            },
            maintainAspectRatio: false,
            responsive: true,
            cutout: 60,
          }}
        />
      </Box>
      <Stack
        sx={styles.percentageLabelWrapper}
        direction="column"
        data-test-id={PERCENTAGE_DOUGHNUT_LABEL_TEST_ID}
      >
        <Box sx={styles.percentageLabel}>
          {showNA ? 'n/a' : `${percentage} %`}
        </Box>
      </Stack>
    </Box>
  );
};

export default PercentageDoughnut;
